import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  writeBatch,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase';

export interface Platform {
  id: string;
  name: string;
  description: string;
  type: 'Desktop' | 'Web' | 'Mobile' | 'API';
  downloadLink?: string;
  website: string;
  logo: string;
  features: string[];
  operatingSystem: string[];
  languages: string[];
  minSystemRequirements?: string;
  developer: string;
  version?: string;
  fileSize?: string;
  price: string;
  license: string;
  category: 'Trading Platform' | 'Analysis Tool' | 'Copy Trading' | 'Mobile App' | 'API/Integration';
  popularity: number; // 1-5 rating
  lastUpdated: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

class FirebasePlatformService {
  private readonly collectionName = 'platforms';
  private readonly collection = collection(db, this.collectionName);

  // Default platforms to initialize the database
  private defaultPlatforms: Omit<Platform, 'id'>[] = [
    {
      name: 'MetaTrader 4',
      description: 'The world\'s most popular forex trading platform with advanced charting, automated trading, and comprehensive analysis tools.',
      type: 'Desktop',
      downloadLink: 'https://download.mql5.com/cdn/web/metaquotes.software.corp/mt4/mt4setup.exe',
      website: 'https://www.metatrader4.com',
      logo: 'https://www.metatrader4.com/favicon.ico',
      features: [
        'Advanced Charting',
        'Expert Advisors (EAs)',
        'Custom Indicators',
        'Strategy Tester',
        'Mobile Trading',
        'Market Analysis',
        'One-Click Trading',
        'Hedging Capabilities'
      ],
      operatingSystem: ['Windows', 'Mac', 'Linux', 'iOS', 'Android'],
      languages: ['English', 'Spanish', 'French', 'German', 'Chinese', 'Japanese', 'Russian', 'Arabic'],
      minSystemRequirements: 'Windows 7 or higher, 512 MB RAM, 50 MB disk space',
      developer: 'MetaQuotes Software Corp.',
      version: '4.00 Build 1403',
      fileSize: '5.2 MB',
      price: 'Free',
      license: 'Freeware',
      category: 'Trading Platform',
      popularity: 5,
      lastUpdated: new Date().toISOString(),
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      name: 'MetaTrader 5',
      description: 'Next-generation multi-asset trading platform for forex, stocks, futures, and CFDs with advanced features and improved performance.',
      type: 'Desktop',
      downloadLink: 'https://download.mql5.com/cdn/web/metaquotes.software.corp/mt5/mt5setup.exe',
      website: 'https://www.metatrader5.com',
      logo: 'https://www.metatrader5.com/favicon.ico',
      features: [
        'Multi-Asset Trading',
        'Advanced Order Types',
        'Economic Calendar',
        'MQL5 Programming',
        'Copy Trading',
        'Market Depth',
        'Technical Analysis',
        'Algorithmic Trading'
      ],
      operatingSystem: ['Windows', 'Mac', 'Linux', 'iOS', 'Android'],
      languages: ['English', 'Spanish', 'French', 'German', 'Chinese', 'Japanese', 'Russian', 'Arabic'],
      minSystemRequirements: 'Windows 10 or higher, 1 GB RAM, 100 MB disk space',
      developer: 'MetaQuotes Software Corp.',
      version: '5.00 Build 4280',
      fileSize: '8.7 MB',
      price: 'Free',
      license: 'Freeware',
      category: 'Trading Platform',
      popularity: 5,
      lastUpdated: new Date().toISOString(),
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      name: 'cTrader',
      description: 'Professional ECN trading platform with advanced charting, level II pricing, and institutional-grade execution.',
      type: 'Desktop',
      downloadLink: 'https://ctrader.com/download',
      website: 'https://ctrader.com',
      logo: 'https://ctrader.com/favicon.ico',
      features: [
        'ECN Trading',
        'Level II Pricing',
        'Advanced Charting',
        'Algorithmic Trading',
        'Copy Trading',
        'Risk Management',
        'Multi-Monitor Support',
        'Cloud Synchronization'
      ],
      operatingSystem: ['Windows', 'Mac', 'iOS', 'Android', 'Web'],
      languages: ['English', 'Spanish', 'French', 'German', 'Chinese', 'Arabic'],
      minSystemRequirements: 'Windows 10 or higher, 2 GB RAM, 200 MB disk space',
      developer: 'Spotware Systems Ltd.',
      version: '4.5.2',
      fileSize: '15.3 MB',
      price: 'Free',
      license: 'Freeware',
      category: 'Trading Platform',
      popularity: 4,
      lastUpdated: new Date().toISOString(),
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      name: 'TradingView',
      description: 'Cloud-based charting and social trading platform with real-time data, advanced analytics, and community features.',
      type: 'Web',
      website: 'https://www.tradingview.com',
      logo: 'https://www.tradingview.com/favicon.ico',
      features: [
        'Real-time Charts',
        'Pine Script',
        'Social Trading',
        'Market Screener',
        'Economic Calendar',
        'News & Analysis',
        'Paper Trading',
        'Multi-Timeframe Analysis'
      ],
      operatingSystem: ['Web Browser', 'Windows', 'Mac', 'iOS', 'Android'],
      languages: ['English', 'Spanish', 'French', 'German', 'Chinese', 'Japanese', 'Russian'],
      developer: 'TradingView Inc.',
      price: 'Freemium',
      license: 'Subscription',
      category: 'Analysis Tool',
      popularity: 5,
      lastUpdated: new Date().toISOString(),
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      name: 'NinjaTrader',
      description: 'Advanced trading platform for active traders with sophisticated charting, strategy development, and market analysis tools.',
      type: 'Desktop',
      downloadLink: 'https://ninjatrader.com/GetStarted',
      website: 'https://ninjatrader.com',
      logo: 'https://ninjatrader.com/favicon.ico',
      features: [
        'Advanced Charting',
        'Strategy Development',
        'Market Replay',
        'Order Flow Analysis',
        'Automated Trading',
        'Risk Management',
        'Market Analytics',
        'Custom Indicators'
      ],
      operatingSystem: ['Windows'],
      languages: ['English'],
      minSystemRequirements: 'Windows 10 or higher, 4 GB RAM, 2 GB disk space',
      developer: 'NinjaTrader LLC',
      version: '8.1.3.1',
      fileSize: '150 MB',
      price: 'Free / $720 lifetime',
      license: 'Freemium',
      category: 'Trading Platform',
      popularity: 4,
      lastUpdated: new Date().toISOString(),
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      name: 'ZuluTrade',
      description: 'Social trading platform that allows you to automatically copy trades from successful traders worldwide.',
      type: 'Web',
      website: 'https://www.zulutrade.com',
      logo: 'https://www.zulutrade.com/favicon.ico',
      features: [
        'Copy Trading',
        'Signal Providers',
        'Risk Management',
        'Performance Analytics',
        'Social Trading',
        'AutoTrade',
        'Portfolio Diversification',
        'Real-time Monitoring'
      ],
      operatingSystem: ['Web Browser', 'iOS', 'Android'],
      languages: ['English', 'Spanish', 'French', 'German', 'Italian', 'Greek'],
      developer: 'ZuluTrade Inc.',
      price: 'Free',
      license: 'Freeware',
      category: 'Copy Trading',
      popularity: 4,
      lastUpdated: new Date().toISOString(),
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  ];

  // Initialize database with default platforms if empty
  async initializeDatabase(): Promise<void> {
    try {
      const snapshot = await getDocs(this.collection);
      if (snapshot.empty) {
        const batch = writeBatch(db);
        this.defaultPlatforms.forEach((platform) => {
          const docRef = doc(this.collection);
          batch.set(docRef, platform);
        });
        await batch.commit();
        console.log('Database initialized with default platforms');
      }
    } catch (error) {
      console.error('Failed to initialize platforms database:', error);
    }
  }

  // Get all platforms
  async getAllPlatforms(): Promise<Platform[]> {
    try {
      await this.initializeDatabase();
      const querySnapshot = await getDocs(query(this.collection, orderBy('createdAt', 'desc')));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Platform));
    } catch (error) {
      console.error('Failed to get platforms:', error);
      return [];
    }
  }

  // Get active platforms only
  async getActivePlatforms(): Promise<Platform[]> {
    try {
      const q = query(this.collection, where('isActive', '==', true), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Platform));
    } catch (error) {
      console.error('Failed to get active platforms:', error);
      return [];
    }
  }

  // Get platform by ID
  async getPlatformById(id: string): Promise<Platform | null> {
    try {
      const docRef = doc(this.collection, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as Platform;
      }
      return null;
    } catch (error) {
      console.error('Failed to get platform by ID:', error);
      return null;
    }
  }

  // Add new platform
  async addPlatform(platformData: Omit<Platform, 'id' | 'createdAt' | 'updatedAt'>): Promise<Platform> {
    try {
      const newPlatformData = {
        ...platformData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(this.collection, newPlatformData);
      
      return {
        id: docRef.id,
        ...newPlatformData
      } as Platform;
    } catch (error) {
      console.error('Failed to add platform:', error);
      throw error;
    }
  }

  // Update platform
  async updatePlatform(id: string, updates: Partial<Omit<Platform, 'id' | 'createdAt' | 'updatedAt'>>): Promise<Platform | null> {
    try {
      const docRef = doc(this.collection, id);
      const updateData = {
        ...updates,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(docRef, updateData);
      
      // Get updated document
      const updatedDoc = await getDoc(docRef);
      if (updatedDoc.exists()) {
        return {
          id: updatedDoc.id,
          ...updatedDoc.data()
        } as Platform;
      }
      return null;
    } catch (error) {
      console.error('Failed to update platform:', error);
      throw error;
    }
  }

  // Delete platform
  async deletePlatform(id: string): Promise<boolean> {
    try {
      const docRef = doc(this.collection, id);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error('Failed to delete platform:', error);
      return false;
    }
  }

  // Toggle platform active status
  async togglePlatformStatus(id: string): Promise<Platform | null> {
    try {
      const docRef = doc(this.collection, id);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }

      const currentData = docSnap.data() as Platform;
      const updateData = {
        isActive: !currentData.isActive,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(docRef, updateData);
      
      return {
        ...currentData,
        ...updateData,
        id: docSnap.id
      } as Platform;
    } catch (error) {
      console.error('Failed to toggle platform status:', error);
      return null;
    }
  }

  // Search platforms
  async searchPlatforms(searchTerm: string): Promise<Platform[]> {
    try {
      const allPlatforms = await this.getAllPlatforms();
      const lowerSearchTerm = searchTerm.toLowerCase();
      
      return allPlatforms.filter(platform =>
        platform.name.toLowerCase().includes(lowerSearchTerm) ||
        platform.description.toLowerCase().includes(lowerSearchTerm) ||
        platform.features.some(feature => feature.toLowerCase().includes(lowerSearchTerm)) ||
        platform.category.toLowerCase().includes(lowerSearchTerm)
      );
    } catch (error) {
      console.error('Failed to search platforms:', error);
      return [];
    }
  }

  // Get platforms by category
  async getPlatformsByCategory(category: Platform['category']): Promise<Platform[]> {
    try {
      const q = query(this.collection, where('category', '==', category), orderBy('popularity', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Platform));
    } catch (error) {
      console.error('Failed to get platforms by category:', error);
      return [];
    }
  }

  // Real-time listener for platforms
  onPlatformsChange(callback: (platforms: Platform[]) => void): () => void {
    const q = query(this.collection, orderBy('createdAt', 'desc'));
    
    return onSnapshot(q, (querySnapshot) => {
      const platforms = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Platform));
      callback(platforms);
    }, (error) => {
      console.error('Error listening to platforms changes:', error);
    });
  }
}

export const firebasePlatformService = new FirebasePlatformService();